import moment from 'moment';
import * as Yup from 'yup';
import { emailRegex, getPhoneNumberRegex } from '../constant/Regex';
import { PayrollFrequencies } from '../types/checkHQPayroll/payroll.types';
import { RestaurantMode } from '../types/restaurant/restaurant.types';
import { dateDifference } from '../utils/utils';
import { stripDate } from '../utils/DateTimeFormats';
import { RECIPIENT_DETAIL_TYPE, USER_GIFT_TYPE } from '../types/gift-card/giftCard.types';
import { AvgPrepTimeLimits } from '../constant/constant';
const EmailvalidationSchema = Yup.object().shape({
  username: Yup.string().required('Email is required').matches(emailRegex, 'Incorrect email address'),
});

const passwordValidationSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Password must include lowercase alphabets')
    .matches(/[A-Z]/, 'Password must include uppercase alphabets')
    .matches(/\d/, 'Password must include at least one number')
    .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must include at least one special character'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), ''], 'Passwords do not match')
    .required('Confirm password is required'),
});

const phoneValidationSchema = Yup.object().shape({
  addPhoneNumber: Yup.string()
    .matches(getPhoneNumberRegex(), 'Invalid phone number format') // only pakistan and America number
    .required('Incorrect phone number'),
});

const loginPasswordValidationSchema = Yup.object().shape({
  password: Yup.string().required('Password is required'),
});

const loginMomValidation = Yup.object().shape({
  password: Yup.string().required('Password is required'),
});

const profileValidationSchema = Yup.object().shape({
  given_name: Yup.string()
    .matches(/^[a-zA-Z\s]*$/, 'Invalid first name')
    .max(15, 'First Name must be most 15 characters')
    .trim()
    .required('Field cannot be empty'),
  family_name: Yup.string()
    .matches(/^[a-zA-Z\s]*$/, 'Invalid last name')
    .max(15, 'Last Name must be most 15 characters')
    .trim()
    .required('Field cannot be empty'),
});

const phoneValidationScheme = Yup.object().shape({
  phone: Yup.string()
    .transform((value) => value.replace(/\D/g, '')) // Remove non-numeric characters
    .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits')
    .required('Phone number is required'),
});

// Validation schema for First name and last name
const personalInfoValidationScehma = Yup.object().shape({
  fName: Yup.string()
    .matches(/^[^0-9!@#$%^&*()_+{}[\]:;<>,.?~/"'`|=-]*$/, 'Incorrect name format')
    .required('First Name is required')
    .max(15, 'Charater limit exceeded'),
  lName: Yup.string()
    .matches(/^[^0-9!@#$%^&*()_+{}[\]:;<>,.?~/"'`|=-]*$/, 'Incorrect name format')
    .nullable()
    .max(25, 'Charater limit exceeded'),
  email: Yup.string()
    .nullable()
    .notRequired()
    .test('is-valid-email', 'Incorrect email address', function (value) {
      if (!value) return true; // skip validation if empty/null
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value); // or use your emailRegex
    }),
});

const addNewEmployeeValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .matches(/^[a-zA-Z\s]*$/, 'Invalid first name')
    .max(14, 'Max character limit reached')
    .trim()
    .required('Field cannot be empty'),
  lastName: Yup.string()
    .matches(/^[a-zA-Z\s]*$/, 'Invalid first name')
    .max(14, 'Max character limit reached')
    .trim()
    .required('Field cannot be empty'),
  emailAddress: Yup.string().required('Email address is required').matches(emailRegex, 'Incorrect email address'),
  phoneNumber: Yup.string().nullable().notRequired().matches(getPhoneNumberRegex(), 'Invalid phone number'), // only pakistan and America number
});

const personalInformationValidationSchema = Yup.object().shape({
  emailAddress: Yup.string().required('Email address is required').matches(emailRegex, 'Incorrect email address'),
});

const BusinessInfovalidationSchema = Yup.object().shape({
  address: Yup.string().required('Address is required'),
  zip: Yup.string()
    .min(5, 'Zip code must be 5 digits')
    .max(5, 'Zip must be 5 digits')
    .typeError('Zip code must be a number')
    .required('Zip code is required'),
});

const RestaurantInfovalidationSchema = Yup.object().shape({
  name: Yup.string().required('Location Name is required'),
  restaurantModes: Yup.string().required('Restaurant location type is required'),
  phoneNo: Yup.string()
    .required('Phone Number is required')
    .matches(getPhoneNumberRegex(), 'Invalid phone number format'), // only pakistan and America number
  building: Yup.string().required('Floor building is required'),
  address: Yup.string().required('Street Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zipCode: Yup.string()
    .min(5, 'Zip code must be 5 digits')
    .max(5, 'Zip must be 5 digits')
    .typeError('Zip code must be a number')
    .required('Zip code is required'),
  otherMode: Yup.string().when('restaurantModes', {
    is: RestaurantMode.Other,
    then: () => Yup.string().required('Other Restaurant Location Type is required'),
  }),
});

const lostAndFoundValidationSchema = (reportType: string) => {
  return Yup.object().shape({
    itemName: Yup.string()
      .required('Item Name is required')
      .max(25, 'Max character limit reached')
      .matches(/^[^\uD800-\uDBFF]+$/, 'Invalid input (emojis not allowed)')
      .test('not-only-spaces', 'Name cannot be empty spaces', (value) => value.trim() !== ''),
    itemCategory: Yup.string().required('Item Category is required'),
    customerName:
      reportType === 'lostItem'
        ? Yup.string()
            .required('Customer Name is required')
            .max(25, 'Max character limit reached')
            .matches(/^[a-zA-Z0-9 ]+$/, 'Invalid input (special characters not allowed)')
            .test('not-only-spaces', 'Name cannot be empty spaces', (value) => value.trim() !== '')
        : Yup.string()
            .max(25, 'Max character limit reached')
            .matches(/^[a-zA-Z0-9 ]+$/, 'Invalid input (special characters not allowed)')
            .test('not-only-spaces', 'Name cannot be empty spaces', (value) => value?.trim() !== ''),
    email:
      reportType === 'lostItem'
        ? Yup.string().required('Email address is required').matches(emailRegex, 'Incorrect email address')
        : Yup.string().matches(emailRegex, 'Incorrect email address'),
    phoneNumber:
      reportType === 'lostItem'
        ? Yup.string()
            .required('Phone Number is required')
            .matches(getPhoneNumberRegex(), 'Invalid phone number format')
        : Yup.string().matches(getPhoneNumberRegex(), 'Invalid phone number format'),
    serviceArea: Yup.string()
      .required('Service Area is required')
      .max(25, 'Max character limit reached')
      .matches(/^[^\uD800-\uDBFF]+$/, 'Invalid input (emojis not allowed)')
      .test('not-only-spaces', 'Name cannot be empty spaces', (value) => value.trim() !== ''),
    tableNumber: Yup.string()
      .matches(/^[a-zA-Z0-9]*$/, 'Invalid input (only alphanumeric characters allowed)')
      .test('not-only-spaces', 'Name cannot be empty spaces', (value) => value?.trim() !== ''),
    primaryColor: Yup.string()
      .required('Primary Color is required')
      .max(25, 'Max character limit reached')
      .test('not-only-spaces', 'Name cannot be empty spaces', (value) => value.trim() !== ''),
    otherColor: Yup.string()
      .required('Other Color is required')
      .max(25, 'Max character limit reached')
      .test('not-only-spaces', 'Name cannot be empty spaces', (value) => value.trim() !== ''),
    description: Yup.string()
      .required('Description is required')
      .max(250, 'Max character limit reached')
      .test('not-only-spaces', 'Name cannot be empty spaces', (value) => value.trim() !== ''),
  });
};

const lostAndFoundRecieverInfoValidationSchema = Yup.object().shape({
  customerName: Yup.string()
    .required('Name is required')
    .max(25, 'Max character limit reached')
    .matches(/^[a-zA-Z0-9 ]+$/, 'Invalid input (special characters not allowed)')
    .test('not-only-spaces', 'Name cannot be empty spaces', (value) => value.trim() !== ''),
  customerEmail: Yup.string().required('Email address is required').matches(emailRegex, 'Incorrect email address'),
  customerPhone: Yup.string()
    .required('Phone number is required')
    .matches(getPhoneNumberRegex(), 'Invalid phone number format'),
});

const docVaultValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .matches(/^[a-zA-Z0-9 .]+$/, 'Invalid input (only letters, numbers, spaces, and periods are allowed)')
    .test('not-only-spaces', 'Name cannot be empty spaces', (value) => value.trim() !== ''),
});

const TimeOffRequestValidationSchema = Yup.object().shape({
  type: Yup.string().required('Please select time off type'),
  timeOffStartDate: Yup.date()
    .min(stripDate(new Date()), 'Start date cannot be in the past')
    .required('Please select start date'),
  timeOffEndDate: Yup.date().when('timeOffStartDate', (timeOffStartDate, schema) => {
    if (timeOffStartDate[0]) {
      return schema
        .test('isNullEndDate', 'Please select end date', function (value) {
          if (value !== null && value !== undefined) {
            return true;
          }
          return false;
        })

        .max(
          new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
          'End date cannot be more than 1 year from today'
        )
        .min(timeOffStartDate, 'End date cannot be before start date')
        .test('max-days', 'You cannot request leave for more than 10 days', function (timeOffEndDate) {
          const startDate = this.parent.timeOffStartDate;
          const endDate = timeOffEndDate;
          const dateDifferenceInDays = (endDate && dateDifference(startDate, endDate)) ?? 0;
          return dateDifferenceInDays <= 10;
        })
        .required('Please select end date');
    } else {
      return schema.typeError('Please select end date').nullable('Please select end date');
    }
  }),
});

const PayScheduleRequestValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  payFrequency: Yup.string()
    .oneOf(Object.values(PayrollFrequencies), 'Invalid pay frequency')
    .required('Pay Frequency is required'),
  firstPeriodEnd: Yup.date().required('Please select first period end date'),
  firstPayday: Yup.date()
    .min(new Date(), 'First pay date cannot be in the past')
    .min(Yup.ref('firstPayday'), 'First period end date before be after first period end')
    .required('Please select first pay date')
    .test('is-weekday', 'First pay day must be a weekday', function (value) {
      const { payFrequency } = this.parent;
      if (payFrequency === PayrollFrequencies.WEEKLY || payFrequency === PayrollFrequencies.BI_WEEKLY) {
        return value?.getDay() !== 0 && value?.getDay() !== 6;
      }
      return true;
    })
    .test(
      'is-after-first-period-end-others',
      'First pay day must be at least 4 days after the first period end date',
      function (value) {
        const { firstPeriodEnd } = this.parent;
        if (value && firstPeriodEnd) {
          const diffDays = moment(value).diff(moment(firstPeriodEnd), 'days');
          return diffDays >= 4;
        }
        return true;
      }
    ),
  status: Yup.string().required('Active status is required'),
});

const OffCyclePayrollRequestValidationSchema = Yup.object().shape({
  payPeriodStartDate: Yup.date().required('Pay period start date is required'),
  payPeriodEndDate: Yup.date()
    .min(Yup.ref('payPeriodStartDate'), 'Pay period end date cannot be before pay period start date')
    .required('Pay period end date is required'),
  payDay: Yup.date()
    .min(new Date(), 'Pay day cannot be in the past')
    .required('Pay day is required')
    // .min the min should be 4 days + payPeriodEndDate
    .test('min-days', 'Pay day should be at least 4 days after pay period end date', function (payDay) {
      const payPeriodEndDate = this.parent.payPeriodEndDate;
      const dateDifferenceInDays = dateDifference(payPeriodEndDate, payDay) ?? 0;
      return dateDifferenceInDays >= 4;
    })
    .test('is-weekday', 'Second pay day must be a weekday', function (value) {
      return value?.getDay() !== 0 && value?.getDay() !== 6;
    })
    .test('within-30-days', 'Pay day should be within 30 days after pay period end date', function (payDay) {
      const payPeriodEndDate = this.parent.payPeriodEndDate;
      const dateDifferenceInDays = dateDifference(payPeriodEndDate, payDay) ?? 0;
      return dateDifferenceInDays <= 30;
    })
    .test('no-weekend-in-between', 'Pay period end and payday should not have a weekend in between', function (payDay) {
      // Pay period end and payday should not have a weekend in between
      const payPeriodEndDate = this.parent.payPeriodEndDate;
      const adjustedEndDate = moment(payDay).subtract(1, 'day').toString();
      const days = dateDifference(payPeriodEndDate, new Date(adjustedEndDate)) ?? 0;
      const todayToPaydayDiff = dateDifference(new Date(), new Date(adjustedEndDate)) ?? 0;
      if (todayToPaydayDiff < 6) {
        const currentDate = moment(payPeriodEndDate);

        let weekendCount = 0;
        for (let i = 0; i < days; i++) {
          if (currentDate.day() === 0 || currentDate.day() === 6) {
            weekendCount++;
          }
          currentDate.add(1, 'day');
        }
        if (
          (days == 4 && weekendCount == 0) ||
          (days === 5 && weekendCount <= 1) ||
          (days === 6 && weekendCount <= 2) ||
          days > 6
        ) {
          return true;
        }
        return false;
      }
      return true;
    }),
  taxAllEarnings: Yup.boolean().required('This field is required'),
  applyBenefits: Yup.boolean().required('This field is required'),
  applyPostTaxDeductions: Yup.boolean().required('This field is required'),
});
const PayBreakdownRequestValidationSchema = Yup.object().shape({
  earningType: Yup.string().required('Earning Type is required'),
  amount: Yup.number()
    .typeError('Amount should be a number')
    .required('Amount is required')
    .positive('Amount must be a positive number')
    .max(99999, 'Amount must be less than $99,999'),
});
const PhoneNumberSchema = Yup.object().shape({
  phoneNo: Yup.string()
    .required('Phone Number is required')
    .matches(getPhoneNumberRegex(), 'Invalid phone number format'),
});

const wastageLogValidationSchema = (isMainRecipe: boolean) =>
  Yup.object().shape({
    wastedQuantity: Yup.number()
      .required('Wasted quantity is required')
      .min(0.1, 'Wasted quantity must be greater than 0')
      .typeError('Wasted quantity must be a number')
      .test(
        'wastedQuantity-less-than-quantityOnHand',
        'Wasted quantity cannot exceed available quantity on hand',
        function (value) {
          const item = this.parent.item;
          return value != null && item?.quantityOnHand != null && !isMainRecipe ? value <= item.quantityOnHand : true;
        }
      ),
    reason: Yup.string().required('Reason is required'),
    comments: Yup.string().max(250, 'Max character limit reached'),
    item: Yup.object().shape({
      id: Yup.number(),
      name: Yup.string(),
      quantityOnHand: Yup.number().nullable(),

      parValue: Yup.number().nullable(),

      unitOfMeasurement: Yup.string(),
    }),
  });
const EmailReceiptSchema = Yup.object().shape({
  email: Yup.string().required('Email address is required').matches(emailRegex, 'Incorrect email address'),
});
const giftCardsPurchaseValidationSchema = () =>
  Yup.object().shape({
    gift_type: Yup.string().required('Form type is required'),
    initial_amount: Yup.number()
      .required('Gift card amount is required')
      .min(1, 'Amount must be greater than zero')
      .max(500, 'Amount can be max 500'),
    purchased_by_name: Yup.string().required('Sender name is required'),
    purchased_by_email: Yup.string().email('Invalid email').required('Sender email is required'),
    purchased_by_phone: Yup.string().required('Sender phone number is required').length(10, 'Incorrect Format'),
    message: Yup.string().max(160, 'Message must not be greater than 160 characters').notRequired(),
    recipient_name: Yup.string().when('gift_type', {
      is: USER_GIFT_TYPE.GIFT,
      then: () => Yup.string().required('Recipient name is required'),
    }),
    recipient_detail_type: Yup.string().when('gift_type', {
      is: USER_GIFT_TYPE.GIFT,
      then: () => Yup.string().required('Recipient detail type is required'),
    }),
    recipient_email: Yup.string().when(['gift_type', 'recipient_detail_type'], {
      is: (gift_type: any, recipient_detail_type: any) =>
        gift_type === USER_GIFT_TYPE.GIFT && recipient_detail_type === RECIPIENT_DETAIL_TYPE.SEND_AS_EMAIL,
      then: () => Yup.string().email('Invalid email').required('Recipient email is required'),
    }),
    recipient_phone: Yup.string().when(['gift_type', 'recipient_detail_type'], {
      is: (gift_type: any, recipient_detail_type: any) =>
        gift_type === USER_GIFT_TYPE.GIFT && recipient_detail_type === RECIPIENT_DETAIL_TYPE.SEND_AS_TEXT,
      then: () => Yup.string().required('Recipient phone number is required').length(10, 'Incorrect Format'),
    }),
    terms_accepted: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
  });

const GeneralSettingsValidationSchema = Yup.object().shape({
  avgPrepTime: Yup.number()
    .typeError('Average preparation time must be a number')
    .required('Average preparation time is required')
    .min(
      AvgPrepTimeLimits.MinInMinutes,
      `Average preparation time must be at least ${AvgPrepTimeLimits.MinInMinutes} minute`
    )
    .max(
      AvgPrepTimeLimits.MaxInMinutes,
      `Average preparation time cannot exceed ${AvgPrepTimeLimits.MaxInMinutes} minutes`
    ),
});

export {
  addNewEmployeeValidationSchema,
  BusinessInfovalidationSchema,
  docVaultValidationSchema,
  EmailvalidationSchema,
  loginPasswordValidationSchema,
  lostAndFoundRecieverInfoValidationSchema,
  lostAndFoundValidationSchema,
  OffCyclePayrollRequestValidationSchema,
  passwordValidationSchema,
  PayBreakdownRequestValidationSchema,
  PayScheduleRequestValidationSchema,
  personalInformationValidationSchema,
  personalInfoValidationScehma,
  PhoneNumberSchema,
  phoneValidationSchema,
  phoneValidationScheme,
  profileValidationSchema,
  RestaurantInfovalidationSchema,
  TimeOffRequestValidationSchema,
  wastageLogValidationSchema,
  loginMomValidation,
  EmailReceiptSchema,
  giftCardsPurchaseValidationSchema,
  GeneralSettingsValidationSchema,
};
